import type Phaser from "phaser";
import type { AssetManifest, CompiledChunk, ManifestMap, ManifestObjectAppearance } from "daedalus-core";
import { createDomSurface, scenePageAtlas, type PageImage } from "daedalus-renderer-phaser";
import type { ShrineArt, LandmarkTally } from "../map-picture/landmarks.ts";
import { chunkFeed } from "./chunk-feed.ts";
import { WorldMapPicture, type DisplaySurface, type GroundSurface } from "./picture.ts";

/**
 * The browser and Phaser side of the world map's picture: canvases for the ground and the display, the atlas pages the
 * scene already holds as the painter's sources, and the feed of the page's own client chunks. Nothing here is fetched
 * or compiled (ADR-100 decision 3).
 */

/**
 * What the page hands over to make the picture of the map it stands on.
 */
export interface PictureWorld {
  readonly chunks: ReadonlyMap<string, CompiledChunk>;
  readonly assets: AssetManifest;
  readonly appearances: readonly ManifestObjectAppearance[];
  readonly chunkSize: number;
  readonly tileSize: number;
  readonly art?: ShrineArt | null;
  readonly tally?: LandmarkTally;
}

/**
 * The name the window carries for a map: the map's own `name` property when it has one, else its world's id with an
 * initial capital ("Aethervale"). A draft for the author: the world's manifest holds no name yet.
 *
 * @param worldId The world's id.
 * @param map The map.
 * @returns The title.
 */
export function mapTitle(worldId: string, map: Pick<ManifestMap, "properties">): string {
  const named = map.properties?.["name"];
  if (typeof named === "string" && named !== "") {
    return named;
  }
  return worldId === "" ? "" : `${worldId.charAt(0).toUpperCase()}${worldId.slice(1)}`;
}

/**
 * Makes a canvas for the ground.
 *
 * @param width Width in pixels.
 * @param height Height in pixels.
 * @returns The surface, which can read a rectangle of its pixels back for the ground's mean tone (P2.12-T32), or null
 *   when the browser gives no 2D context.
 */
function ground(width: number, height: number): GroundSurface<PageImage> | null {
  const surface = createDomSurface(width, height, true);
  if (surface === null) {
    return null;
  }
  const canvas = surface.canvas;
  return {
    ...surface,
    source: canvas,
    read: (x, y, w, h) => {
      try {
        return canvas.getContext("2d")?.getImageData(x, y, w, h).data ?? null;
      } catch {
        return null;
      }
    },
  };
}

/**
 * Makes the canvas the display is composed on.
 *
 * @param width Width in pixels.
 * @param height Height in pixels.
 * @returns The surface, which can put a rectangle of finished pixels (the blurred ground of a fogged cell, ADR-100
 *   decision 63), or null when the browser gives no 2D context.
 */
function display(width: number, height: number): DisplaySurface<PageImage> | null {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  return context === null
    ? null
    : {
        context,
        source: canvas,
        put: (pixels, x, y, w, h) => context.putImageData(new ImageData(pixels as Uint8ClampedArray<ArrayBuffer>, w, h), x, y),
      };
}

/**
 * Makes the picture of a map from the chunks and atlas pages the scene already holds.
 *
 * @param scene The scene whose texture manager holds the atlas pages.
 * @param map The map.
 * @param world The chunks and what they are drawn against.
 * @returns The picture, not yet started.
 */
export function createWorldMapPicture(scene: Phaser.Scene, map: Pick<ManifestMap, "id" | "width" | "height">, world: PictureWorld): WorldMapPicture<PageImage> {
  return new WorldMapPicture<PageImage>({
    map: { id: map.id, width: map.width, height: map.height },
    chunkSize: world.chunkSize,
    tileSize: world.tileSize,
    manifest: world.assets,
    feed: chunkFeed({ chunks: world.chunks, manifest: world.assets, appearances: world.appearances, chunkSize: world.chunkSize, tileSize: world.tileSize, art: world.art ?? null, ...(world.tally === undefined ? {} : { tally: world.tally }) }),
    atlas: scenePageAtlas(scene, world.assets),
    ground,
    display,
  });
}
